import React from 'react';
import { useTranslation } from 'react-i18next';
import { fmtNum, fmtPct, fmtHM } from '../../lib/formatHelpers';

/**
 * V2 HeroIntegrated — Carte héro principale (admin pilotage).
 *
 * NoiRock adaptation : on affiche les KPIs Phase 1 disponibles via les tables
 * non chiffrées (creator counts, valid_go_live_days, live_duration_seconds,
 * live_streams). Les colonnes financières (diamonds_enc) seront ajoutées
 * quand l'Edge Function generic-decrypt-metrics sera déployée (Sprint 5).
 *
 * Props :
 *  - period            : 'today' | 'yesterday' | 'week' | 'month'
 *  - activeCreators    : nb créateurs actifs sur la période
 *  - totalCreators     : nb total créateurs
 *  - sessionsCount     : sum live_streams sur la période
 *  - hoursTotal        : sum live_duration_seconds / 3600
 *  - validGoLiveDays   : sum valid_go_live_days
 *  - newRecruits       : nb nouveaux créateurs (joined dans la période)
 *  - loading
 */
const KpiCell = ({ label, value, sub }) => (
  <div className="flex-1 min-w-0">
    <div className="text-[10px] uppercase tracking-widest text-muted-foreground/70 font-semibold">{label}</div>
    <div className="text-2xl md:text-3xl font-heading font-bold text-foreground tabular-nums mt-0.5 truncate">{value}</div>
    {sub && <div className="text-[10px] text-muted-foreground mt-0.5 truncate">{sub}</div>}
  </div>
);

const HeroIntegrated = ({
  period = 'month',
  activeCreators = 0,
  totalCreators = 0,
  sessionsCount = 0,
  hoursTotal = 0,
  validGoLiveDays = 0,
  newRecruits = 0,
  loading = false,
}) => {
  const { t } = useTranslation();
  const activePct = totalCreators > 0 ? (activeCreators / totalCreators) * 100 : null;

  return (
    <section className="rounded-2xl bg-card border border-border overflow-hidden">
      {/* Top : period label + active ratio */}
      <div className="px-5 md:px-6 py-4 border-b border-border bg-surface-2 flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground/70 font-semibold">
            {t('adminV2.modules.hero.label', 'Pilotage')}
          </div>
          <div className="text-sm font-heading text-foreground mt-0.5">
            {t(`adminV2.periods.${period}`, period)}
          </div>
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground/70 font-semibold">
            {t('adminV2.modules.hero.active', 'Active')}
          </div>
          <div className="text-2xl font-heading font-bold text-foreground tabular-nums mt-0.5">
            {loading ? '…' : fmtNum(activeCreators)}
            <span className="text-sm text-muted-foreground font-normal"> / {fmtNum(totalCreators)}</span>
          </div>
          {activePct != null && (
            <div className="text-[10px] text-muted-foreground mt-0.5">{fmtPct(activePct, 0)}</div>
          )}
        </div>
      </div>

      {/* Bottom : KPI strip */}
      <div className="px-5 md:px-6 py-5 grid grid-cols-2 md:grid-cols-4 gap-4">
        <KpiCell
          label={t('adminV2.modules.hero.kpi.sessions', 'Sessions')}
          value={loading ? '…' : fmtNum(sessionsCount)}
          sub={t(`adminV2.periods.${period}`, period)}
        />
        <KpiCell
          label={t('adminV2.modules.hero.kpi.hours', 'Live hours')}
          value={loading ? '…' : fmtHM(hoursTotal * 3600)}
        />
        <KpiCell
          label={t('adminV2.modules.hero.kpi.goLiveDays', 'Valid go-live days')}
          value={loading ? '…' : fmtNum(validGoLiveDays)}
        />
        <KpiCell
          label={t('adminV2.modules.hero.kpi.newRecruits', 'New recruits')}
          value={loading ? '…' : fmtNum(newRecruits)}
        />
      </div>
    </section>
  );
};

export default HeroIntegrated;
